import { MinusIcon, PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useRef } from 'react';
import { Button, Group, Label } from 'react-aria-components';
import { cn } from '@/lib/utils';

interface InputWithEndButtonsProps {
	label?: string;
	value?: number | string;
	onChange?: (value: string) => void;
	onBlur?: (value: string) => void;
	onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	minValue?: number;
	maxValue?: number;
	step?: number;
	className?: string;
	/** Classes for the bordered group around the input and buttons (e.g. a border colour). */
	groupClassName?: string;
	inputClassName?: string;
	id?: string;
	/** Id(s) of elements describing the input, e.g. a hint below it. */
	ariaDescribedBy?: string;
	testId?: string;
	inputRef?: React.RefObject<HTMLInputElement | null>;
	disabled?: boolean;
	readOnly?: boolean;
	type?: React.HTMLInputTypeAttribute;
	inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
	pattern?: string;
	// Allow custom validation - if provided, validation is checked before calling onChange
	validateOnChange?: (value: string) => boolean;
}

const InputWithEndButtons = ({
	label,
	value,
	onChange,
	onBlur,
	onInput,
	onKeyDown,
	minValue = 0,
	maxValue,
	step = 0.1,
	className,
	groupClassName,
	inputClassName,
	id,
	ariaDescribedBy,
	testId,
	inputRef: externalInputRef,
	disabled = false,
	readOnly = false,
	type = 'text',
	inputMode,
	pattern,
	validateOnChange
}: InputWithEndButtonsProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	// Store the value captured on mouse down (before blur fires)
	const capturedValueRef = useRef<string | null>(null);
	// Flag to indicate we're in the middle of an increment/decrement operation
	const isButtonPressInProgressRef = useRef<boolean>(false);

	// Calculate decimal places from step to avoid floating point precision issues
	const getDecimalPlaces = (stepValue: number): number => {
		if (stepValue % 1 === 0) return 0;
		const stepStr = stepValue.toString();
		if (stepStr.includes('e')) {
			// Handle scientific notation
			const match = stepStr.match(/e-(\d+)/);
			return match ? Number.parseInt(match[1], 10) : 0;
		}
		const decimalIndex = stepStr.indexOf('.');
		return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1;
	};

	const decimalPlaces = getDecimalPlaces(step);

	// Capture the input value on press start (mouse down), before blur fires
	const captureValue = useCallback(() => {
		if (inputRef.current) {
			capturedValueRef.current = inputRef.current.value;
			// Mark that a button press is in progress (to skip blur)
			isButtonPressInProgressRef.current = true;
		}
	}, []);

	const handleIncrement = useCallback(() => {
		if (disabled || readOnly || !inputRef.current) return;
		// Use the value captured on press start (before blur modified it)
		const valueToUse = capturedValueRef.current ?? inputRef.current.value;
		// Clear the captured value and button press flag
		capturedValueRef.current = null;
		isButtonPressInProgressRef.current = false;
		// Normalize comma to period for parsing (accept both input formats)
		const normalizedValue = String(valueToUse || '0').replace(',', '.');
		const currentValue = Number.parseFloat(normalizedValue);
		const newValue = Math.min(currentValue + step, maxValue ?? Infinity);
		// Round to avoid floating point precision issues
		const roundedValue = Math.round(newValue * 10 ** decimalPlaces) / 10 ** decimalPlaces;
		// Format with period as decimal separator
		const stringValue = roundedValue.toFixed(decimalPlaces);
		if (!validateOnChange || validateOnChange(stringValue)) {
			if (onChange) {
				onChange(stringValue);
			}
			// Update DOM directly for immediate feedback
			if (inputRef.current) {
				inputRef.current.value = stringValue;
				inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
			}
		}
	}, [disabled, readOnly, step, maxValue, decimalPlaces, validateOnChange, onChange]);

	const handleDecrement = useCallback(() => {
		if (disabled || readOnly || !inputRef.current) return;
		// Use the value captured on press start (before blur modified it)
		const valueToUse = capturedValueRef.current ?? inputRef.current.value;
		// Clear the captured value and button press flag
		capturedValueRef.current = null;
		isButtonPressInProgressRef.current = false;
		// Normalize comma to period for parsing (accept both input formats)
		const normalizedValue = String(valueToUse || '0').replace(',', '.');
		const currentValue = Number.parseFloat(normalizedValue);
		const newValue = Math.max(currentValue - step, minValue);
		// Round to avoid floating point precision issues
		const roundedValue = Math.round(newValue * 10 ** decimalPlaces) / 10 ** decimalPlaces;
		// Format with period as decimal separator
		const stringValue = roundedValue.toFixed(decimalPlaces);
		if (!validateOnChange || validateOnChange(stringValue)) {
			if (onChange) {
				onChange(stringValue);
			}
			// Update DOM directly for immediate feedback
			if (inputRef.current) {
				inputRef.current.value = stringValue;
				inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
			}
		}
	}, [disabled, readOnly, step, minValue, decimalPlaces, validateOnChange, onChange]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		// If custom validation is provided, use it
		if (!validateOnChange || validateOnChange(inputValue)) {
			if (onChange) {
				onChange(inputValue);
			}
		}
	};

	const setInputRef = useCallback(
		(node: HTMLInputElement | null) => {
			inputRef.current = node;
			if (externalInputRef) {
				externalInputRef.current = node;
			}
		},
		[externalInputRef]
	);

	return (
		<div className={cn('w-full space-y-2', className)}>
			{label && (
				<Label className="flex items-center gap-2 text-sm leading-none font-medium select-none">
					{label}
				</Label>
			)}
			<Group
				className={cn(
					'dark:bg-input/30 border-input data-[focus-within]:border-ring data-[focus-within]:ring-ring/50 data-[focus-within]:has-aria-invalid:ring-destructive/20 dark:data-[focus-within]:has-aria-invalid:ring-destructive/40 data-[focus-within]:has-aria-invalid:border-destructive relative inline-flex h-9 w-full min-w-0 items-center overflow-hidden rounded-md border bg-transparent text-base whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[focus-within]:ring-[3px] md:text-sm',
					groupClassName
				)}
			>
				<input
					ref={setInputRef}
					id={id}
					aria-describedby={ariaDescribedBy}
					data-testid={testId}
					type={type}
					inputMode={inputMode}
					pattern={pattern}
					value={value ?? ''}
					disabled={disabled}
					readOnly={readOnly}
					className={cn(
						'selection:bg-primary selection:text-primary-foreground w-full grow px-3 py-2 text-center tabular-nums outline-none bg-transparent',
						inputClassName
					)}
					onChange={handleInputChange}
					onBlur={(e) => {
						// Skip blur if we're in the middle of a button press
						// (the increment/decrement will handle the value update)
						if (isButtonPressInProgressRef.current) {
							return;
						}
						if (onBlur) {
							onBlur(e.target.value);
						}
					}}
					onInput={onInput}
					onKeyDown={onKeyDown}
				/>
				<div className="h-full w-px bg-border flex-shrink-0" />
				<div className="flex flex-col h-full flex-shrink-0">
					<Button
						onPressStart={captureValue}
						onPress={handleIncrement}
						isDisabled={disabled || readOnly}
						excludeFromTabOrder
						className="bg-background text-muted-foreground hover:bg-accent hover:text-foreground flex h-1/2 min-h-0 w-8 items-center justify-center rounded-none border-0 text-sm transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 first:rounded-tr-md"
					>
						<PlusIcon className="size-3" />
						<span className="sr-only">Increment</span>
					</Button>
					<div className="h-px w-full bg-border flex-shrink-0" />
					<Button
						onPressStart={captureValue}
						onPress={handleDecrement}
						isDisabled={disabled || readOnly}
						excludeFromTabOrder
						className="bg-background text-muted-foreground hover:bg-accent hover:text-foreground flex h-1/2 min-h-0 w-8 items-center justify-center rounded-none border-0 text-sm transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 last:rounded-br-md"
					>
						<MinusIcon className="size-3" />
						<span className="sr-only">Decrement</span>
					</Button>
				</div>
			</Group>
		</div>
	);
};

export default InputWithEndButtons;
